import { LocalizedValidation } from "../language";
import { fillTemplateString } from "../utility";
import { LegacyJsonResponseSuccess, RequiredFields, ValidationResult, Validator } from "./system";

export type RegistrationOperation = 'Register' | 'Deregister'

export type RegistrationResponse = LegacyJsonResponseSuccess<ParticipantRegisterData>

export interface ParticipantDeregisterData {
	SeatsLeft: number
	MaximumParticipants: number
}

export interface ParticipantRegisterData extends ParticipantDeregisterData {
	ParticipantId: number
}

export interface ParticipantWorkbooks {
	availablePreviousWorkbooks: Array<WorkbookData>
	participantData: Participant
}

export interface WorkbookData {
	Name: string
	ProgramName: string
	Workbooks: Array<Workbook>
}

export interface Workbook {
	WorkbookId: number
	Name: string
	Description: string
}

export interface RelatedParticipant {
	RegistrationId: number
	NameFirst: string
	NameLast: string
}

export const NewBlankRelatedParticipant: RelatedParticipant = {
	RegistrationId: -1, 
	NameFirst: '', 
	NameLast: ''
}

export interface Participant {
	AccessibilityRequirement: string
	AddressLine1: string
	AddressLine2: string
	City: string
	CommodityCategoryId: number
	CommoditySubCategoryId: number
	DateCreated: Date
	Email: string
	IsCertifiedOrganic: boolean
	IsOrganicCertificationPending: boolean
	LanguageId: number
	MunicipalityTier1Id: number
	MunicipalityTier2Id: number
	NameFirst: string
	NameLast: string
	RelatedParticipants: Array<RelatedParticipant>
	Phone1: number
	Phone1Extension: number
	Phone2: number
	Phone2Extension: number
	PostalCode: string
	ProvinceId: number
	RegistrationId: number
	WatershedId: number
	ParticipantId: number
	PublicUserIdLink: number
}

const ParticipantRequiredFieldsData: Array<keyof Participant> = [
	'LanguageId', 'NameFirst', 'NameLast', 'AddressLine1', 'City', 'ProvinceId', 
	'PostalCode', 'MunicipalityTier1Id', 'MunicipalityTier2Id', 'Phone1', 'IsCertifiedOrganic', 
	'IsOrganicCertificationPending', 'WatershedId', 'CommodityCategoryId', 'CommoditySubCategoryId'
]

export const ParticipantRequiredFields: RequiredFields<Participant> = new Set(ParticipantRequiredFieldsData)

class ValidatorLogic {
	constructor(private validationText: LocalizedValidation) {}

	// zero counts as a value here
	checkPresent = (val: any) => 
		(val !== 0) && !val && this.validationText.FieldRequired || undefined

	// some dropdown values have zero values as default unselected
	checkPresentAndNonFalsey = (val: any) => 
		!val && this.validationText.FieldRequired || undefined

	checkMaxLength = (val: string, maxLength: number) => 
		val && val.length > maxLength && fillTemplateString(this.validationText.FieldTooLong, { maxLength }) || undefined

	checkMax = (val: number, max: number) => 
		val > max && fillTemplateString(this.validationText.FieldTooLarge, { max }) || undefined

	checkMin = (val: number, min: number) => 
		val < min && fillTemplateString(this.validationText.FieldTooSmall, { min }) || undefined

	checkRange = (val: number, min: number, max: number) => 
		val && (this.checkMin(val, min) || this.checkMax(val, max)) && fillTemplateString(this.validationText.FieldOutsideRange, { min, max })

	checkPhoneNumber = (val: number) => 
		val && !val.toString().match(/^\d{10}$/) && this.validationText.InvalidPhoneNumber || undefined

	checkPhoneExtension = (val: number) => 
		val && !val.toString().match(/^\d{1,5}$/) && this.validationText.InvalidPhoneExtension || undefined

	checkPostalCode = (val: string) => {
		const regex = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z]\s?\d[ABCEGHJ-NPRSTV-Z]\d$/i
		return val && !val.match(regex) && this.validationText.InvalidPostalCode || undefined
	}

	checkEmail = (val: string) => {
		const regex = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/
		return val && !val.match(regex) && this.validationText.InvalidEmail || undefined
	}
}

// TODO: clean this up so that it's a generalized abstract class
// Or simplify somehow
export class RelatedParticipantValidator implements Validator<RelatedParticipant> {
	private _logic: ValidatorLogic

	constructor(validationText: LocalizedValidation) {
		this._logic = new ValidatorLogic(validationText)
	}

	RegistrationId =  (val: number, obj?: RelatedParticipant | undefined, prop?: "RegistrationId" | undefined) => undefined

	NameFirst = (val: string, obj?: RelatedParticipant | undefined, prop?: "NameFirst" | undefined) => this._logic.checkPresent(val)

	NameLast = (val: string, obj?: RelatedParticipant | undefined, prop?: "NameLast" | undefined) => this._logic.checkPresent(val)

	validate(participant?: RelatedParticipant, invalidIterator?: (prop: keyof RelatedParticipant, validationMessage: string) => void): ValidationResult<RelatedParticipant> {
		let validResult: ValidationResult<RelatedParticipant> = { _isValid: true }

		// it's okay to have no additional participant
		if (!participant) return validResult

		// otherwise we check its properties
		const validationResult = (Object.keys(this)).reduce((result, propName) => {
			// hack to avoid typing issues at this point
			const anyThis = this as any, anyResult = result as any, anyParticipant = participant as any

			if (propName != '_logic' && propName != 'validate') {
				anyResult[propName] = anyThis[propName](anyParticipant[propName])
				const propIsValid = !anyResult[propName]
				anyResult._isValid &&= propIsValid

				// let user perform an action if we've received an invalidity
				invalidIterator?.(propName as any, anyResult[propName])
			}
			return anyResult as ValidationResult<RelatedParticipant>

		}, validResult)

		return validationResult
	}
}

// TODO: clean this up so that it's a generalized abstract class
export class ParticipantValidator implements Validator<Participant> {
	private _logic: ValidatorLogic

	constructor(validationText: LocalizedValidation) {
		this._logic = new ValidatorLogic(validationText)
	}

	validate(participant: Participant, invalidIterator?: (prop: keyof Participant, validationMessage: string) => void): ValidationResult<Participant> {

		const validationResult = (Object.keys(this)).reduce((result, propName) => {
			// hack to avoid typing issues at this point
			const anyThis = this as any, anyResult = result as any, anyParticipant = participant as any

			if (propName != '_logic' && propName != 'validate') {
				anyResult[propName] = anyThis[propName](anyParticipant[propName])
				const propIsValid = !anyResult[propName]
				anyResult._isValid &&= propIsValid

				// let user perform an action if we've received an invalidity
				invalidIterator?.(propName as any, anyResult[propName])
			}
			return anyResult as ValidationResult<Participant>

		}, { _isValid: true } as any as ValidationResult<Participant>)

		return validationResult
	}

	AccessibilityRequirement = (val: string) => this._logic.checkMaxLength(val, 200)

	AddressLine1 = (val: string) => this._logic.checkPresent(val) || this._logic.checkMaxLength(val, 100)

	AddressLine2 = (val: string) => this._logic.checkMaxLength(val, 100)

	City = (val: string) => this._logic.checkPresent(val) || this._logic.checkMaxLength(val, 100)

	CommodityCategoryId = (val: number) => this._logic.checkPresentAndNonFalsey(val)
	CommoditySubCategoryId = (val: number) => this._logic.checkPresentAndNonFalsey(val)

	Email = (val: string) => this._logic.checkMaxLength(val, 100) || this._logic.checkEmail(val)

	// checkboxes
	IsCertifiedOrganic = (val: boolean) => undefined
	IsOrganicCertificationPending = (val: boolean) => undefined

	// Handle valid values via interface dropdown
	LanguageId = (val: number) => this._logic.checkPresentAndNonFalsey(val)

	// Handle valid values via interface dropdown
	MunicipalityTier1Id = (val: number) => this._logic.checkPresentAndNonFalsey(val)
	MunicipalityTier2Id = (val: number) => this._logic.checkPresentAndNonFalsey(val)

	NameFirst = (val: string) => this._logic.checkPresent(val) || this._logic.checkMaxLength(val, 100)
	NameLast = (val: string) => this._logic.checkPresent(val) || this._logic.checkMaxLength(val, 100)

	RelatedParticipants = (val: RelatedParticipant[]) => undefined

	Phone1 = (val: number) => 
		this._logic.checkPresent(val) || this._logic.checkPhoneNumber(val) 

	Phone1Extension = (val: number) => this._logic.checkPhoneExtension(val)

	Phone2 = (val: number) => this._logic.checkPhoneNumber(val)
	Phone2Extension = (val: number) => this._logic.checkPhoneExtension(val)

	PostalCode = (val: string) => 
		this._logic.checkPresent(val) || this._logic.checkPostalCode(val) 

	// Handle valid values via interface dropdown
	ProvinceId = (val: number) => this._logic.checkPresentAndNonFalsey(val)
	WatershedId = (val: number) => this._logic.checkPresentAndNonFalsey(val)

	// system fields
	ParticipantId = (val: number) => undefined
	PublicUserIdLink = (val: number) => undefined
	RegistrationId = (val: number) => undefined
	DateCreated = (val: Date) => undefined

}